import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useInfiniteQuery, } from '@marvelapp/ballpark-components';
import { mapFilterNullish } from '@marvelapp/core';
const GET_LIVE_PROJECT_SESSIONS = gql `
  query getLiveProjectSessions($pk: Int, $cursor: String) {
    userTest(pk: $pk) {
      pk
      uuid
      prototype {
        isLocked
        pk
      }
      liveSessions(first: 25, after: $cursor) @connection(key: "liveSessions") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            observers {
              avatarUrl
              displayName
            }
            researchers {
              avatarUrl
              displayName
            }
            shareLinks {
              panelShareLink
            }
            testRecording {
              egressStartTime
              pk
              roomName
              roomStartTime
            }
            participant {
              name
            }
          }
        }
      }
    }
  }
`;
export function useLiveProjectSessions() {
    var _a, _b, _c, _d, _e;
    const match = useRouteMatch();
    const projectPk = match.params.id;
    const { data, fetchMore, networkStatus, refetch } = useQuery(GET_LIVE_PROJECT_SESSIONS, {
        variables: { pk: Number(projectPk) },
    });
    const isFetchingMore = networkStatus !== NetworkStatus.ready;
    const isInitialLoad = networkStatus === NetworkStatus.loading;
    const getConnection = useCallback((data) => { var _a; return (_a = data === null || data === void 0 ? void 0 : data.userTest) === null || _a === void 0 ? void 0 : _a.liveSessions; }, []);
    const { fetchNextPage, hasNextPage } = useInfiniteQuery({
        data,
        fetchMore,
        getDataConnection: getConnection,
        isFetching: isFetchingMore,
    });
    const userTest = (data === null || data === void 0 ? void 0 : data.userTest)
        ? {
            uuid: data.userTest.uuid,
            prototype: {
                isLocked: (_b = (_a = data.userTest.prototype) === null || _a === void 0 ? void 0 : _a.isLocked) !== null && _b !== void 0 ? _b : false,
                pk: ((_c = data.userTest.prototype) === null || _c === void 0 ? void 0 : _c.pk) || null,
            },
        }
        : null;
    const liveSessions = useMemo(() => {
        var _a, _b, _c;
        return mapFilterNullish((_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.userTest) === null || _a === void 0 ? void 0 : _a.liveSessions) === null || _b === void 0 ? void 0 : _b.edges) !== null && _c !== void 0 ? _c : [], (edge) => {
            var _a;
            if (!(edge === null || edge === void 0 ? void 0 : edge.node))
                return null;
            const { node: liveSession } = edge;
            const { observers, researchers, testRecording, participant, shareLinks, } = liveSession;
            if (!testRecording || !shareLinks)
                return null;
            return {
                observers: formatUserInfo(observers),
                moderators: formatUserInfo(researchers),
                participantName: (_a = participant === null || participant === void 0 ? void 0 : participant.name) !== null && _a !== void 0 ? _a : '',
                shareUrl: shareLinks.panelShareLink,
                startTime: testRecording.egressStartTime || testRecording.roomStartTime,
                status: testRecording.egressStartTime ? 'Live' : 'Waiting',
            };
        });
    }, [(_e = (_d = data === null || data === void 0 ? void 0 : data.userTest) === null || _d === void 0 ? void 0 : _d.liveSessions) === null || _e === void 0 ? void 0 : _e.edges]);
    const refetchSessions = useCallback(() => {
        refetch({ pk: projectPk });
    }, [refetch, projectPk]);
    return {
        isLoadingLiveSessions: isInitialLoad,
        liveSessions,
        userTest,
        fetchNextPage,
        hasNextPage,
        isFetchingMore,
        networkStatus,
        refetchSessions,
    };
}
function formatUserInfo(users) {
    if (!users)
        return [];
    return mapFilterNullish(users, (user) => {
        if (!user || !user.displayName)
            return null;
        const { avatarUrl, displayName } = user;
        return {
            avatarUrl: avatarUrl !== null && avatarUrl !== void 0 ? avatarUrl : '',
            displayName,
        };
    });
}
